/**
 * ChatRefinar — terminal AMIGÁVEL de refino (substitui o ttyd/claude-code cru).
 *
 * Painel de chat: o usuário descreve o ajuste em linguagem natural; cada mensagem
 * roda 1 turno de `claude --print` no workdir editando o RASCUNHO_BLOCOS.json. O
 * painel de blocos ao vivo (à esquerda, no RefinoSplit) reflete as edições na hora.
 * Sem token de terminal — autenticado pelo cookie admin. Multi-turno via --continue.
 *
 * "💾 Salvar e fechar" reusa /finalizar-refino (versiona os blocos + renderiza .docx).
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { usePecasStatusRapido } from '../hooks';
import { ProgressoPipeline } from './ProgressoPipeline';

interface Props {
  idPrazo: number;
  numeroProcesso?: string;
  onClose: () => void;
  onFinalizado?: () => void;
  embedded?: boolean;
}

type Msg = { role: 'user' | 'assistant' | 'sistema'; texto: string };

export function ChatRefinar({ idPrazo, numeroProcesso, onClose, onFinalizado, embedded }: Props) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [iniciando, setIniciando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [salvouOk, setSalvouOk] = useState(false);
  const [erroFinalizar, setErroFinalizar] = useState<string | null>(null);
  const [houveEdicao, setHouveEdicao] = useState(false);  // só versiona se algum prompt foi enviado
  const fimRef = useRef<HTMLDivElement>(null);

  const status = usePecasStatusRapido(idPrazo, finalizando);

  // Prepara o workdir (baixa a última versão dos blocos) ao abrir.
  useEffect(() => {
    let vivo = true;
    setIniciando(true);
    api.refinarIniciar(idPrazo)
      .then(() => { if (vivo) setMsgs([{ role: 'sistema', texto: 'Pronto. Descreva o ajuste que você quer na peça (ex.: "reforce a tese da equiparação no tópico 2", "remova a preliminar de incompetência", "deixe o pedido de honorários mais enxuto"). Os blocos à esquerda atualizam ao vivo.' }]); })
      .catch((e) => { if (vivo) setMsgs([{ role: 'sistema', texto: `Falha ao preparar o refino: ${e?.message ?? 'erro'}. Gere o mapa/blocos antes de refinar.` }]); })
      .finally(() => { if (vivo) setIniciando(false); });
    return () => { vivo = false; };
  }, [idPrazo]);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs, enviando]);

  // Última seleção emitida pelo FalcaoPanel (window event). O comando
  // "inclua [...] entre N e M" enriquece o prompt com o JSON dos itens.
  const falcaoSelRef = useRef<any[]>([]);
  useEffect(() => {
    function onSel(ev: any) {
      if (ev?.detail?.idPrazo === idPrazo) falcaoSelRef.current = ev.detail.selecionadas ?? [];
    }
    window.addEventListener('falcao:selection', onSel as any);
    return () => window.removeEventListener('falcao:selection', onSel as any);
  }, [idPrazo]);

  // Detecta a intenção de INSERIR a(s) jurisprudência(s) já selecionada(s) no
  // painel do Falcão. Antes o gatilho era SÓ a frase exata "entre N e M"; isso
  // fazia o conteúdo selecionado NUNCA chegar ao Claude quando o usuário pedia
  // "insira no tópico 2" / "use a selecionada" → o Claude re-pesquisava sozinho.
  function querInserirJuris(texto: string): boolean {
    const verbo = /\b(inclu|inser|insir|adicion|acrescent|coloc|p[õo]e[r]?|cit[ae]|cita[r]?|use|usar|utiliz|aplic|traz|traga|junt|anex|embas|fundament|refor[çc])/i.test(texto);
    const alvo = /\b(jurisprud|precedent|ement|ac[óo]rd[ãa]o|julgad|selecionad[ao]?|sele[çc][ãa]o|marcad[ao]s?|falc[ãa]o|esses?|essas?|est[ae]s?)\b/i.test(texto);
    return verbo && alvo;
  }

  // Interpreta, de forma flexível, ONDE inserir. Aceita "entre N e M",
  // "no tópico/parágrafo N", "após N", "no final", ou nada (default temático).
  function instrucaoDePosicao(texto: string): string {
    const mEntre = texto.match(/\bentre\s+(?:o\s+)?(?:par[áa]grafo\s+|t[óo]pico\s+|p#?\s*)?(\d+)\s+e\s+(?:o\s+)?(?:par[áa]grafo\s+|t[óo]pico\s+|p#?\s*)?(\d+)\b/i);
    const mApos = texto.match(/\b(?:ap[óo]s|depois\s+d[eo]|abaixo\s+d[eo]|logo\s+ap[óo]s|no|ao)\s+(?:[oa]\s+)?(?:t[óo]pico|par[áa]grafo|item|n[ºo]\.?|p#?)?\s*(\d+)\b/i);
    const final = /\b(no\s+)?final\b|ao\s+fim\b|no\s+fim\b|antes\s+(?:d[oa]\s+)?(?:pedido|fecho|assinatura|conclus)/i.test(texto);
    if (mEntre) {
      const n = mEntre[1];
      return `Insira os novos blocos LOGO APÓS o parágrafo P#${n} (ou seja, entre P#${n} e P#${mEntre[2]}). ` +
        `Conte SOMENTE os blocos com type == "P" na ordem do JSON; P#${n} é o ${n}º bloco "P".`;
    }
    if (mApos) {
      const n = mApos[1];
      return `Insira os novos blocos LOGO APÓS o parágrafo P#${n}. ` +
        `Conte SOMENTE os blocos com type == "P" na ordem do JSON; P#${n} é o ${n}º bloco "P".`;
    }
    if (final) {
      return 'Insira os novos blocos no FINAL da fundamentação: imediatamente ANTES do primeiro bloco ' +
        'de type == "PEDIDO" (se não houver, antes do bloco type == "ASSINATURA"). Nunca depois da assinatura.';
    }
    return 'Insira os novos blocos no ponto TEMÁTICO mais pertinente segundo o pedido do usuário ' +
      '(logo após o parágrafo que trata da mesma matéria das ementas). Na dúvida, insira ao FINAL da ' +
      'fundamentação, imediatamente antes do primeiro bloco type == "PEDIDO" (ou antes da ASSINATURA). ' +
      'Nunca depois da assinatura.';
  }

  function enriquecerComFalcao(texto: string): string {
    const sel = falcaoSelRef.current;
    // Só enriquece quando há item(ns) marcado(s) E o pedido é de inserção de
    // jurisprudência. Caso contrário, segue como refino comum (não sequestra
    // edições que nada têm a ver com o painel do Falcão).
    if (!sel?.length || !querInserirJuris(texto)) return texto;

    // Lista LITERAL das ementas selecionadas — o LLM vai copiar o texto desses
    // itens para dentro de blocos CITACAO no RASCUNHO_BLOCOS.json. Não pode
    // resumir nem parafrasear (citação jurisprudencial precisa do teor exato).
    // A ementa já vem decodificada do backend (sem entidades HTML &ccedil; etc.);
    // só removemos os marcadores de destaque "==" do Obsidian.
    const lista = sel.map((j: any, i: number) => {
      const cab = [j.tribunal, j.orgao_julgador, j.numero].filter(Boolean).join(' · ');
      const corpoCru = String(j.ementa || j.snippet || j.inteiro_teor || '').replace(/==/g, '').trim();
      const corpo = corpoCru.slice(0, 2000);
      const meta = [j.data, j.relator ? `Rel. ${j.relator}` : ''].filter(Boolean).join(' · ');
      return `  ${i + 1}) ${cab}${meta ? ` (${meta})` : ''}\n     ${corpo}`;
    }).join('\n\n');

    return [
      '### INSTRUÇÃO ESTRUTURADA — INSERÇÃO DE JURISPRUDÊNCIA (Falcão)',
      '',
      `Edite \`RASCUNHO_BLOCOS.json\` no diretório atual para INSERIR ${sel.length} ` +
        `precedente(s) jurisprudencial(is) que o operador JÁ SELECIONOU no painel do Falcão. ` +
        `O texto das ementas está LISTADO ABAIXO — use-o; NÃO pesquise de novo, NÃO invente outros precedentes.`,
      '',
      'ONDE INSERIR:',
      `- ${instrucaoDePosicao(texto)}`,
      '',
      'BLOCOS A INSERIR (nessa ordem). ⚠️ As chaves do JSON são "type" e "text" (em inglês), igual aos demais blocos do arquivo:',
      '  1. 1 bloco { "type": "P", "text": "Nesse sentido, a jurisprudência da Justiça do Trabalho:" }',
      '  2. Para CADA precedente listado abaixo, 1 bloco { "type": "CITACAO", "text": "<CABEÇALHO>\\n\\n<EMENTA LITERAL>" }',
      '     - O CABEÇALHO é a primeira linha "TRIBUNAL · ÓRGÃO · NÚMERO · (DATA · Rel. NOME)".',
      '     - A EMENTA deve ser o texto LITERAL fornecido (não resumir, não parafrasear).',
      '  3. 1 bloco { "type": "P", "text": "Aplicável, portanto, o entendimento consolidado ao caso." }',
      '',
      'REGRAS:',
      '- Use SEMPRE as chaves "type" e "text" (NUNCA "tipo"/"texto" — o renderizador só lê "type"/"text").',
      '- Mantenha o JSON válido (lista de objetos).',
      '- Preserve todos os demais blocos intactos (não reordene, não renumere, não reescreva).',
      '- Não rode `./salvar.sh` (a UI salva).',
      '',
      'PRECEDENTES SELECIONADOS (use o texto LITERAL na ementa):',
      lista,
      '',
      'No fim, responda em 1–2 frases: quantos blocos foram inseridos e em que posição (ex.: "Inseri 4 blocos após P#18: 1 P de transição + 2 CITACAO + 1 P de fechamento").',
      '',
      '### PEDIDO ORIGINAL DO USUÁRIO',
      texto,
    ].join('\n');
  }

  async function enviar() {
    const textoBruto = input.trim();
    if (!textoBruto || enviando || iniciando) return;
    const texto = enriquecerComFalcao(textoBruto);
    setInput('');
    setMsgs((m) => [...m, { role: 'user', texto: textoBruto }]);
    setHouveEdicao(true);   // houve prompt → "Salvar e fechar" deve versionar
    setEnviando(true);
    // Cria a bolha do assistente VAZIA e vai preenchendo com o echo da LLM (stream).
    let idxAssistant = -1;
    setMsgs((m) => { idxAssistant = m.length; return [...m, { role: 'assistant', texto: '' }]; });
    let acumulado = '';
    let recebeuAlgo = false;
    try {
      await api.refinarChatStream(idPrazo, texto, {
        onDelta: (t) => {
          recebeuAlgo = true;
          acumulado += t;
          setMsgs((m) => m.map((msg, i) => (i === idxAssistant ? { ...msg, texto: acumulado } : msg)));
        },
        onDone: (full) => {
          // texto final autoritativo (substitui o acumulado do stream)
          const finalTexto = (full || acumulado || '(sem resposta)').trim();
          setMsgs((m) => m.map((msg, i) => (i === idxAssistant ? { ...msg, texto: finalTexto } : msg)));
        },
        onErro: (e) => {
          setMsgs((m) => m.map((msg, i) =>
            (i === idxAssistant ? { role: 'sistema', texto: `⚠ ${e}` } : msg)));
        },
      });
      if (!recebeuAlgo) {
        // Stream não entregou nada (ex.: proxy sem SSE) → fallback síncrono.
        const r = await api.refinarChat(idPrazo, texto);
        setMsgs((m) => m.map((msg, i) =>
          (i === idxAssistant ? { ...msg, texto: r.resposta || '(sem resposta)' } : msg)));
      }
    } catch (e: any) {
      setMsgs((m) => m.map((msg, i) =>
        (i === idxAssistant ? { role: 'sistema', texto: `⚠ ${e?.message ?? 'erro no refino'}` } : msg)));
    } finally {
      setEnviando(false);
    }
  }

  async function salvar() {
    // Nada foi enviado nesta sessão → não há o que versionar; só fecha.
    if (!houveEdicao) { onClose(); return; }
    setErroFinalizar(null);
    setSalvouOk(false);
    setFinalizando(true);
    try {
      await api.finalizarRefino(idPrazo);
      setSalvouOk(true);
      onFinalizado?.();
      setTimeout(() => onClose(), 750);
    } catch (e: any) {
      setErroFinalizar(e?.message ?? 'Falha ao salvar as alterações');
      setFinalizando(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
  }

  const painel = (
    <div
      className={
        embedded
          ? 'bg-slate-50 dark:bg-slate-950 h-full w-full flex flex-col overflow-hidden'
          : 'bg-slate-50 dark:bg-slate-950 border border-amber-400/30 rounded-lg shadow-2xl w-full max-w-3xl h-[90vh] flex flex-col overflow-hidden'
      }
      onClick={(e) => e.stopPropagation()}
    >
      {/* Cabeçalho */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2 h-2 rounded-full ${iniciando ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
            ✏️ Refinar peça · {numeroProcesso ?? `prazo #${idPrazo}`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={salvar}
            disabled={finalizando || iniciando}
            className="flex items-center gap-1 rounded bg-green-600 hover:bg-green-500 disabled:opacity-50 px-2.5 py-1 text-[11px] font-medium text-white"
            title="Fecha e versiona os blocos."
          >
            {salvouOk ? '✓ Salvo — fechando…' : finalizando ? '⏳ Salvando…' : (houveEdicao ? '💾 Salvar e fechar' : '✕ Fechar')}
          </button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl leading-none px-1" title="Fechar refino">
            ×
          </button>
        </div>
      </div>

      {erroFinalizar && (
        <div className="px-4 py-1.5 text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800">
          ✗ {erroFinalizar}
        </div>
      )}

      {/* Mensagens */}
      <div className="relative flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
        {msgs.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-blue-600 text-white px-3 py-2 text-sm whitespace-pre-wrap'
                  : m.role === 'assistant'
                  ? 'max-w-[85%] rounded-2xl rounded-bl-sm bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm whitespace-pre-wrap'
                  : 'max-w-[90%] mx-auto text-center text-[11px] text-slate-500 dark:text-slate-400 italic px-2'
              }
            >
              {m.texto}
            </div>
          </div>
        ))}
        {(enviando || iniciando) && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-400">
              <span className="inline-flex gap-1">
                <span className="animate-bounce">•</span>
                <span className="animate-bounce" style={{ animationDelay: '0.15s' }}>•</span>
                <span className="animate-bounce" style={{ animationDelay: '0.3s' }}>•</span>
              </span>
              <span className="ml-2 text-xs">{iniciando ? 'preparando…' : 'refinando os blocos…'}</span>
            </div>
          </div>
        )}
        <div ref={fimRef} />

        {/* Overlay de progresso do "Salvar e fechar" */}
        {finalizando && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur-sm p-5">
            <div className="w-full max-w-md">
              <ProgressoPipeline
                etapa={salvouOk ? 'salvando' : (status.data?.progresso_etapa ?? 'salvando')}
                pct={salvouOk ? 100 : (status.data?.progresso_pct ?? null)}
                msg={salvouOk ? '✓ Versionado — fechando…' : (status.data?.progresso_msg ?? 'Versionando os blocos…')}
              />
            </div>
          </div>
        )}
      </div>

      {/* Caixa de entrada */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-2 bg-white dark:bg-slate-900">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={iniciando || finalizando}
            rows={2}
            placeholder={iniciando ? 'preparando o refino…' : 'Descreva o ajuste e Enter para enviar (Shift+Enter = nova linha)…'}
            className="flex-1 resize-none rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:border-blue-500 disabled:opacity-50"
          />
          <button
            onClick={enviar}
            disabled={enviando || iniciando || !input.trim()}
            className="shrink-0 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white px-4 py-2 text-sm font-medium"
          >
            {enviando ? '⏳' : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  );

  if (embedded) return painel;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-3 sm:p-6" onClick={onClose}>
      {painel}
    </div>
  );
}
